import axios from "axios";
import { useEffect, useState } from "react";

import { sendLogoutForm } from "@/api/auth";
import { restProvider } from "@/api/rest";
import { StatusChip } from "@/components/ui/StatusChip";
import { getEmailFromToken } from "@/lib/decode-token";
import { hasMessage } from "@/lib/error";
import { AuthContextDto } from "@/types/auth";

export const AuthedAs = () => {
  const [authAs, setAuthAs] = useState("");
  const [runAs, setRunAs] = useState(false);
  const [impersonatorName, setImpersonatorName] = useState<string | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    // Try to get info from /api/self first (has correct Run As data)
    axios
      .get<AuthContextDto>("/api/self")
      .then(({ data }) => {
        setAuthAs(data.username);
        setRunAs(data.runAs);
        setImpersonatorName(data.impersonatorName ?? null);
      })
      .catch(() => {
        // Fallback to token if API fails
        setAuthAs(getEmailFromToken());
      });
  }, []);

  const { exitRunAs } = restProvider();

  const handleLogout = async () => {
    try {
      await sendLogoutForm();
    } catch (e: unknown) {
      setError(hasMessage(e) ? e.message : "Logout error");
    }
  };

  const handleExitRunAs = async () => {
    try {
      await exitRunAs();
    } catch (e: unknown) {
      setError(hasMessage(e) ? e.message : "Exit impersonation error");
    }
  };

  if (!authAs) {
    return null;
  }

  return (
    <div className="mt-6 text-center text-sm">
      {runAs && (
        <div className="mb-3">
          <StatusChip tone="warning">
            {impersonatorName ? `Impersonated by ${impersonatorName}` : "Impersonating User"}
          </StatusChip>
        </div>
      )}
      <p className="text-muted-foreground">
        You are logged in as <strong className="font-semibold text-foreground">{authAs}</strong>
      </p>
      <button
        className="mt-2 text-sm text-primary-fg hover:underline bg-transparent border-none cursor-pointer"
        onClick={runAs ? handleExitRunAs : handleLogout}
      >
        {runAs ? "Exit Impersonation" : "Logout"}
      </button>
      {error && <div className="mt-3 text-xs text-destructive-fg">{error}</div>}
    </div>
  );
};
